import React from 'react';

import {createDraftsObservable, draftsLocalForage} from '../store';

const emptyDrafts = {};

export function useDrafts() {
    const [drafts, setDrafts] = React.useState<Record<string, any>>(emptyDrafts);
    const [isReady, setIsReady] = React.useState(false);
    const mountedRef = React.useRef(false);

    React.useEffect(() => {
        mountedRef.current = true;

        let subscription: Subscription | null = null;

        draftsLocalForage
            .ready()
            .then(() => {
                if (!mountedRef.current) {
                    return;
                }

                const draftsObservable = createDraftsObservable();

                (async function fillInitialState() {
                    const initialDrafts: Record<string, any> = {};

                    await draftsLocalForage.iterate((value: string, key) => {
                        initialDrafts[key] = value;
                    });

                    if (!mountedRef.current) {
                        return;
                    }

                    setDrafts((state) => ({...state, ...initialDrafts}));
                    setIsReady(true);
                }());

                subscription = draftsObservable.subscribe({
                    next: (args) => {
                        if (!mountedRef.current) {
                            return;
                        }

                        const {key, newValue, methodName, valueChange} = args;

                        if (!valueChange) {
                            return;
                        }

                        setDrafts((state) => {
                            const newState = {...state, [key]: newValue};

                            if (methodName === 'removeItem') {
                                delete newState[key];
                            }

                            return newState;
                        });
                    },
                    error: (e) => {
                        if (mountedRef.current) {
                            setIsReady(false);
                        }

                        // eslint-disable-next-line no-console
                        console.error(`[Time Drafts]: Observable error ${e}`);
                    },
                    complete: () => {
                        // eslint-disable-next-line no-console
                        console.log('[Time Drafts]: Unsubscribed from drafts update');
                    },
                });
            })
            .catch((e) => {
                if (mountedRef.current) {
                    setIsReady(false);
                }

                // eslint-disable-next-line no-console
                console.error(`[Time Drafts]: Failed: ${e.message}`);
            });

        return () => {
            mountedRef.current = false;
            if (subscription && 'unsubscribe' in subscription && typeof subscription.unsubscribe === 'function') {
                subscription.unsubscribe();
                subscription = null;
            }
        };
    }, []);

    return [drafts, isReady] as const;
}
